/** TanStack Query hooks for personal lesson analysis, refinement, and generation flows. */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { post } from '../client';
import type { AnalysisResult } from '@formency/shared';
import { jobKeys } from './useJobs';
import { subscriptionKeys } from './useSubscription';

// ─── Types ─────────────────────────────────────────────────────────────────

interface AnalyzeContentParams {
  prompt?: string;
  context?: string;
  errorCorrection?: boolean;
  file?: File;
}

interface RefineAnalysisParams {
  analysisId: string;
  feedback: string;
}

interface GenerateFromAnalysisParams {
  analysisId: string;
  suggestionIndex: number;
  imagesEnabled?: boolean;
}

interface GenerateResult {
  id: string;
  type: string;
  status: string;
}

// ─── Hooks ─────────────────────────────────────────────────────────────────

/**
 * Analyse a prompt or file and get lesson suggestions.
 * Sends multipart/form-data when file is present, JSON otherwise.
 */
export function useAnalyzeContent() {
  return useMutation({
    mutationFn: async (params: AnalyzeContentParams): Promise<AnalysisResult> => {
      if (params.file) {
        // Multipart — Axios auto-detects FormData
        const formData = new FormData();
        formData.append('file', params.file);
        if (params.prompt) formData.append('prompt', params.prompt);
        if (params.context) formData.append('context', params.context);
        if (params.errorCorrection) formData.append('errorCorrection', 'true');

        // Explicit Content-Type override: the Axios instance defaults to application/json,
        // which prevents automatic FormData boundary detection.
        return post<AnalysisResult>('/me/personal-lessons/analyze', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }

      // JSON body (prompt-only flow)
      return post<AnalysisResult>('/me/personal-lessons/analyze', {
        prompt: params.prompt,
        context: params.context,
        errorCorrection: params.errorCorrection,
      });
    },
  });
}

/**
 * Refine analysis suggestions using user feedback.
 */
export function useRefineAnalysis() {
  return useMutation({
    mutationFn: async (params: RefineAnalysisParams): Promise<AnalysisResult> => {
      return post<AnalysisResult>(`/me/personal-lessons/analyze/${params.analysisId}/refine`, {
        feedback: params.feedback,
      });
    },
  });
}

/**
 * Generate a lesson from a selected analysis suggestion.
 * Returns 202 with background job record (same shape as existing generate).
 */
export function useGenerateFromAnalysis() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: GenerateFromAnalysisParams): Promise<GenerateResult> => {
      return post<GenerateResult>(`/me/personal-lessons/analyze/${params.analysisId}/generate`, {
        suggestionIndex: params.suggestionIndex,
        ...(params.imagesEnabled !== undefined && { imagesEnabled: params.imagesEnabled }),
      });
    },
    onSuccess: () => {
      // Invalidate personal lessons list and subscription usage so counts refresh
      queryClient.invalidateQueries({ queryKey: ['f', 'personal-lessons'] });
      void queryClient.invalidateQueries({ queryKey: subscriptionKeys.all });
      // Invalidate jobs so the toast hook picks up the new generation job
      void queryClient.invalidateQueries({ queryKey: jobKeys.all });
    },
  });
}
